import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { Injectable } from "@angular/core";
import { CubiqWorker } from "../../worker/CubiqWorker";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { ScratchPadDataLookupService } from "shared/cubiq-services/lookup/model/impl/ScratchPadDataLookupService";
import { ScratchPadDataPrimaryLookupService } from "shared/cubiq-services/lookup/model/impl/ScratchPadDataPrimaryLookupService";
import { OrderDataPromiseLookupService } from "shared/cubiq-services/lookup/model/impl/OrderDataPromiseLookupService";

import { TRADING_SOURCE_INTERNAL } from "shared/cubiq-services/model/TradingSource.constants";
import { v4 as uuid } from "uuid";
import { AllocationBulkingService } from "shared/cubiq-services/action/impl/AllocationBulkingService";
/**
 * All primitive fields used to make a key for matching bulk-compatible orders. Compound fields
 * e.g. fixingDate, fixingTime, CustomValues are explivitly referenced elsewhere
 */
const BULK_BENCMARK_KEY_FIELDS: string[] = [
  "base",
  "nonBase",
  "fixedCcy",
  "clientCode",
  "fixingSource",
  "comment",
  "comment2",
  "markup",
  "stpHold",
  "traderGroup",
  "isPrivateOrder",
  "useDefaultClientDataFg",
  "markupType",
  "oboClient",
  "tradingChannel",
  "fwdNettingId",
];

@Injectable()
export class ScratchPadDataActionPrimaryService {
  private bulkLimitConfig: number = 100;

  constructor(
    private worker: CubiqWorker,
    protected lookup: ScratchPadDataPrimaryLookupService,
    protected orderDataService: OrderDataPromiseLookupService,
    private bulkingService: AllocationBulkingService,
  ) {}

  //USED BY THE ORDER ENTRY
  saveNewOrder(
    request:
      | CubiqTS.LinkedOrderRequestImpl
      | CubiqTS.LinkedOrderModificationRequestImpl
      | CubiqTS.FixingOrderRequestImpl
      | CubiqTS.FixingOrderModificationRequestImpl,
  ): Promise<any> {
    if (request instanceof CubiqTS.LinkedOrderRequestImpl) request.scratchPadOrder = true;
    else if (
      request instanceof CubiqTS.LinkedOrderModificationRequestImpl ||
      request instanceof CubiqTS.FixingOrderRequestImpl ||
      request instanceof CubiqTS.FixingOrderModificationRequestImpl
    )
      request.savedOrder = true;

    this.lookup.getBase().processTradingChannel(request);

    return this.saveBulkOrders([request], null);
  }

  private saveBulkOrders(requests: Array<any>, orderIds: Array<string>): Promise<any> {
    let bulkRequest: CubiqTS.BulkOrderRequestImpl = new CubiqTS.BulkOrderRequestImpl();
    requests.forEach(this.preprocessOrderRequest.bind(this));
    bulkRequest.orderRequests = requests;
    bulkRequest.ordersToActivate = orderIds;

    return new Promise<CubiqTS.BulkOrderLifeCycleEventImpl>((res, rej) => {
      this.worker
        .postServiceRequest(bulkRequest)
        .then((value: CubiqTS.BulkOrderLifeCycleEventImpl) => {
          if (value instanceof CubiqTS.BulkOrderLifeCycleEventImpl) {
            this.lookup.processBulkOrderLifecycleEvent(
              value as CubiqTS.BulkOrderLifeCycleEventImpl,
            );
          }
          res(value);
        });
    });
  }

  private preprocessOrderRequest(request: any): void {
    if (request.fixingOrderData != null) {
      request.fixingOrderData.useDefaultClientDataFg = false;
      request.fixingOrderData.orderId =
        request.fixingOrderData.orderId == "" ? null : request.fixingOrderData.orderId;
      if (request.fixingOrderData.tradingChannel == null) {
        request.fixingOrderData.tradingChannel = TRADING_SOURCE_INTERNAL;
      }

      if (request.fixingOrderData.orderId != null) {
        let oldReq: CubiqTS.FixingOrderRequestImpl = this.lookup
          .getBase()
          .getItem(request.fixingOrderData.externalClientReference);
        if (
          oldReq != null &&
          oldReq.fixingOrderData.allocations != null &&
          oldReq.fixingOrderData.allocations.length > 0
        ) {
          let allocationRequest: CubiqTS.AllocationRequestDataImpl =
            oldReq.fixingOrderData.allocations[0];
          if (
            request.fixingOrderData.allocations == null &&
            allocationRequest.allocationReferenceId != null
          ) {
            let newAllocation: CubiqTS.AllocationRequestDataImpl =
              new CubiqTS.AllocationRequestDataImpl();
            let fixingOrder: CubiqTS.FixingOrderRequestDataImpl =
              request.fixingOrderData as CubiqTS.FixingOrderRequestDataImpl;
            newAllocation.allocationReferenceId = allocationRequest.allocationReferenceId;
            newAllocation.accountCode = fixingOrder.accountCode;
            newAllocation.oboAccount = fixingOrder.oboAccount;
            newAllocation.allocationIntent = fixingOrder.clientIntent;
            newAllocation.allocationQty = fixingOrder.fixedAmount;
            newAllocation.markupQty = fixingOrder.markup;
            newAllocation.markupType = fixingOrder.markupType;

            newAllocation.fixedCcy = fixingOrder.fixedCcy;
            newAllocation.settleTenor = fixingOrder.tenor;
            newAllocation.settleDate = fixingOrder.valueDate;

            fixingOrder.allocations = [newAllocation];
          }
        }
      }
    } else if (request.rootOrder != null) {
      if (request.rootOrder.tradingChannel == null) {
        request.rootOrder.tradingChannel = TRADING_SOURCE_INTERNAL;
      }
      request.rootOrder.useDefaultClientDataFg = false;
      request.rootOrder.orderId =
        request.rootOrder.orderId == "" ? null : request.rootOrder.orderId;
    }
  }

  saveOrders(extReferences: string[]): Promise<any> {
    let base: ScratchPadDataLookupService = this.lookup.getBase();
    let requestList: Array<any> = extReferences.map((ref) => {
      let item = base.getItemByReference(ref);
      if (item instanceof CubiqTS.FixingOrderRequestImpl) {
        item.fixingOrderData.useDefaultClientDataFg = false;
        if (item.fixingOrderData.tradingChannel == null) {
          item.fixingOrderData.tradingChannel = TRADING_SOURCE_INTERNAL;
        }
        item.savedOrder = true;
      } else {
        if (item instanceof CubiqTS.LinkedOrderModificationRequestImpl) item.savedOrder = true;
        else item.scratchPadOrder = true;
        item.rootOrder.useDefaultClientDataFg = false;
        if (item.rootOrder.tradingChannel) {
          item.rootOrder.tradingChannel = TRADING_SOURCE_INTERNAL;
        }
      }
      return item;
    });
    return this.saveBulkOrders(requestList, null);
  }

  submitOrders(extReferences: string[]): Promise<any> {
    let base: ScratchPadDataLookupService = this.lookup.getBase();
    let requestList: Array<
      | CubiqTS.FixingOrderRequestImpl
      | CubiqTS.LinkedOrderModificationRequestImpl
      | CubiqTS.LinkedOrderRequestImpl
    > = [];
    let idsList: Array<string> = [];
    extReferences.forEach((ref) => {
      this.lookup.addOrderEntryReference(ref);
      let item = base.getItemByReference(ref);
      if (item instanceof CubiqTS.FixingOrderRequestImpl) {
        item.savedOrder = false;
        if (item.fixingOrderData.orderId && item.fixingOrderData.orderId != "")
          idsList.push(item.fixingOrderData.orderId);
        else requestList.push(item);
      } else {
        item.scratchPadOrder = false;
        if (item.rootOrder.orderId && item.rootOrder.orderId != "")
          idsList.push(item.rootOrder.orderId);
        else requestList.push(item);
      }
    });
    return this.saveBulkOrders(requestList, idsList);
  }

  submitBulkedWithConfigOrders(extReferences: string[]): Promise<any> {
    return new Promise<any>((res, rej) => {
      let base: ScratchPadDataLookupService = this.lookup.getBase();
      let arrOfArr: Array<Array<any>> = this.arrayTo2DArray2(extReferences, this.bulkLimitConfig);
      let promiseList: Array<Promise<any>> = [];
      let isFirstTime: boolean = true;
      arrOfArr.forEach((list) => {
        let requestList: Array<
          | CubiqTS.FixingOrderRequestImpl
          | CubiqTS.LinkedOrderModificationRequestImpl
          | CubiqTS.LinkedOrderRequestImpl
        > = [];
        let idsList: Array<string> = [];
        list.forEach((ref) => {
          this.lookup.addOrderEntryReference(ref);
          let item = base.getItemByReference(ref);
          if (item instanceof CubiqTS.FixingOrderRequestImpl) {
            item.savedOrder = false;
            if (item.fixingOrderData.orderId && item.fixingOrderData.orderId != "")
              idsList.push(item.fixingOrderData.orderId);
            else requestList.push(item);
          } else {
            item.scratchPadOrder = false;
            if (item.rootOrder.orderId && item.rootOrder.orderId != "")
              idsList.push(item.rootOrder.orderId);
            else requestList.push(item);
          }
        });
        if (isFirstTime) {
          isFirstTime = false;
          promiseList.push(
            new Promise<any>((res) => {
              res(this.saveBulkOrders(requestList, idsList));
            }),
          );
        } else {
          promiseList.push(
            new Promise<any>((res) => {
              setTimeout(() => {
                res(this.saveBulkOrders(requestList, idsList));
              }, 200);
            }),
          );
        }
      });
      Promise.all(promiseList).then((values) => {
        res(values);
      });
    });
  }

  deleteOrders(orderIds: Array<string>): Promise<any> {
    return new Promise<any>((res, rej) => {
      let base: ScratchPadDataLookupService = this.lookup.getBase();
      let orders: Array<any> = orderIds
        .map((id: string) => base.getItemByReference(id))
        .filter((item: any) => item != null);
      let request: CubiqTS.OrderDeleteRequestImpl = new CubiqTS.OrderDeleteRequestImpl();
      let unsavedOrderIds: Array<string> = orders
        .filter((order: CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl) => {
          if (order instanceof CubiqTS.LinkedOrderRequestImpl)
            return !order.rootOrder.orderId || order.rootOrder.orderId != "";
          if (order instanceof CubiqTS.FixingOrderRequestImpl)
            return !order.fixingOrderData.orderId || order.fixingOrderData.orderId != "";
        })
        .map((order: CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl) => {
          if (order instanceof CubiqTS.LinkedOrderRequestImpl)
            return order.rootOrder.externalClientReference;
          if (order instanceof CubiqTS.FixingOrderRequestImpl)
            return order.fixingOrderData.externalClientReference;
        });
      let savedOrderIds: Array<string> = orders
        .filter((order: CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl) => {
          if (order instanceof CubiqTS.LinkedOrderRequestImpl)
            return order.rootOrder.orderId && order.rootOrder.orderId != "";
          if (order instanceof CubiqTS.FixingOrderRequestImpl)
            return order.fixingOrderData.orderId && order.fixingOrderData.orderId != "";
        })
        .map((order: CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl) => {
          if (order instanceof CubiqTS.LinkedOrderRequestImpl)
            return order.rootOrder.externalClientReference;
          if (order instanceof CubiqTS.FixingOrderRequestImpl)
            return order.fixingOrderData.externalClientReference;
        });
      request.orderIds = orders
        .filter((order: CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl) => {
          if (order instanceof CubiqTS.LinkedOrderRequestImpl)
            return order.rootOrder.orderId && order.rootOrder.orderId != "";
          if (order instanceof CubiqTS.FixingOrderRequestImpl)
            return order.fixingOrderData.orderId && order.fixingOrderData.orderId != "";
        })
        .map((order: CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl) => {
          if (order instanceof CubiqTS.LinkedOrderRequestImpl) return order.rootOrder.orderId;
          if (order instanceof CubiqTS.FixingOrderRequestImpl) return order.fixingOrderData.orderId;
        });
      if (request.orderIds && Array.isArray(request.orderIds) && request.orderIds.length > 0) {
        this.worker.postServiceRequest(request).then((response: any) => {
          savedOrderIds.forEach((id) => {
            base.deleteItemByReference(id);
            this.lookup.fireModelDeleteEvent(id);
          });
          res(response);
        });
      }
      if (unsavedOrderIds && unsavedOrderIds.length > 0) {
        unsavedOrderIds.forEach((id) => {
          base.deleteItemByReference(id);
          this.lookup.fireModelDeleteEvent(id);
        });
      }
    });
  }

  importOrders(fileName: string, content: CubiqTS.ByteBuffer): Promise<any> {
    return new Promise<any>((res, rej) => {
      let request: CubiqTS.OrderImportRequestImpl = new CubiqTS.OrderImportRequestImpl();
      request.fileName = fileName;
      request.uploadId = this.generateRandomId();
      request.streamData = CubiqTS.ByteBuffer.wrap(content.buffer);
      this.worker.postServiceRequest(request).then((response: any) => {
        res(response);
      });
    });
  }

  private generateRandomId(): string {
    return uuid();
  }

  private arrayTo2DArray2(list, howMany): Array<Array<any>> {
    let idx = 0;
    let result = [];

    while (idx < list.length) {
      if (idx % howMany === 0) result.push([]);
      result[result.length - 1].push(list[idx++]);
    }
    return result;
  }

  /**
   * For all referenced multi-allocation benchmark orders, split each order into component orders,
   * one ofor each allocation
   * @param orderIds
   * @returns
   */
  public unbulkBenchmarkOrder(orderIds: string[]): Promise<CubiqTS.FixingOrderRequestImpl[]> {
    return new Promise<CubiqTS.FixingOrderRequestImpl[]>((res, rej) => {
      let newItems: CubiqTS.FixingOrderRequestImpl[] = [];
      const toAdd: any[] = [];
      const toDelete: string[] = [];
      const baseLookup: ScratchPadDataLookupService = this.lookup.getBase();
      orderIds.forEach((orderId: string) => {
        let request = baseLookup.getItem(orderId);
        if (request instanceof CubiqTS.FixingOrderRequestImpl) {
          // If the BM is multiallocation then we create a new order
          // for each allocation and remove the old order
          const order = request.fixingOrderData;
          const nearAllocations = order.allocations ?? [];
          const farAllocations = order.farAllocations ?? [];
          const allAllocations = nearAllocations.concat(farAllocations);
          if (allAllocations.length > 1) {
            allAllocations.forEach((allocation) => toAdd.push({ request, allocation }));
            toDelete.push(orderId);
          }
        }
      });

      // For every allocation in toAdd, we clone the order using the allocation data
      toAdd.forEach(({ request, allocation }) => {
        const newOrder = this.lookup.cloneBMOrderFromAllocation(request, allocation);
        newItems.push(newOrder);
      });
      toDelete.forEach((orderId) => {
        this.deleteOrderReference(orderId);
      });
      this.addNewBMOrderRequests(newItems);
      res(newItems);
    });
  }

  /**
   * Remove the rereferenced order from the data service
   * @param orderKey
   */
  private deleteOrderReference(orderKey: string): void {
    this.lookup.deleteItemById(orderKey);
    this.lookup.fireModelDeleteEvent(orderKey);
  }

  /**
   * Add the referenced orders to the data service
   * @param newOrders
   */
  private addNewBMOrderRequests(newOrders: CubiqTS.FixingOrderRequestImpl[]) {
    const addEvent = new CubiqTS.SavedOrderEventListImpl();
    addEvent.fixingOrderRequests = newOrders;
    this.lookup.processSavedOrderEventList(addEvent);
  }

  /**
   * For all referenced multi-allocation benchmark orders, split each order into component orders,
   * one ofor each allocation
   * @param orderIds
   * @returns
   */
  public bulkBenchmarkOrders(orderIds: string[]): Promise<CubiqTS.FixingOrderRequestImpl[]> {
    return new Promise<CubiqTS.FixingOrderRequestImpl[]>((res, rej) => {
      // Partition all orders into groups with matching bulking Ids
      const baseLookup: ScratchPadDataLookupService = this.lookup.getBase();

      // First we partition by key - this excludes difference sin autoForwardPricing as Spot orders can have either value for this
      const bulkGroups: Map<string, BulkableOrder[]> = new Map();
      let bulkableCount = 0;
      orderIds.forEach((orderId: string) => {
        let request = baseLookup.getItem(orderId);
        if (request instanceof CubiqTS.FixingOrderRequestImpl) {
          const order = request.fixingOrderData;
          const nearAllocations = order.allocations ?? [];
          const farAllocations = order.farAllocations ?? [];
          const allAllocations = nearAllocations.concat(farAllocations);
          if (allAllocations.length == 1) {
            const bulkKey = this.getBulkingKey(order);
            let orders = bulkGroups.get(bulkKey);
            if (!orders) {
              orders = [];
              bulkGroups.set(bulkKey, orders);
            }
            const allocation = Object.assign(
              new CubiqTS.AllocationRequestDataImpl(),
              allAllocations[0],
            );
            if (typeof allocation.settleTenor === "number") {
              allocation.settleDate = null;
            }
            orders.push({ request, allocation: allocation });
            bulkableCount++;
          }
        }
      });
      // At this point we have grouped similar orders but with ignoring autoForwardPricing value
      // - fwd orders with different autoForwardPricing values cannot be grouped, but
      // spot orders can be grouped with fwd orders regardless of their autoForwardPricing flag

      // For every group, if the group has fwds with both true and false values for autoForwardPricing, then we need
      // to create a separate group for those with autoForwardPricing =FALSE (SPOT and autoforwardPricing = true can be grouped together)
      const mixedKeys: Set<string> = new Set();
      bulkGroups.forEach(
        (value: BulkableOrder[], key: string, map: Map<string, BulkableOrder[]>) => {
          let hasFalse = false;
          let hasTrue = false;
          for (const order of value) {
            if (order.allocation.settleTenor != CubiqTS.Tenor.SPOT) {
              hasFalse = hasFalse || !order.request.fixingOrderData.autoForwardPricing;
              hasTrue = hasTrue || order.request.fixingOrderData.autoForwardPricing;
            }
          }
          if (hasFalse && hasTrue) {
            mixedKeys.add(key);
          }
        },
      );
      // Final take each group that we know have mixed fwd autopricing and split those into 2 groups:
      // 1. SPOT + autoFwdPricing = false
      // 2. autoFwdPricing = true
      for (const key of mixedKeys) {
        const origOrders = bulkGroups.get(key);
        bulkGroups.delete(key);
        const falseOrders = [];
        const trueOrders = [];
        for (const order of origOrders) {
          if (
            order.allocation.settleTenor == CubiqTS.Tenor.SPOT ||
            !order.request.fixingOrderData.autoForwardPricing
          ) {
            falseOrders.push(order);
          } else {
            trueOrders.push(order);
          }
        }
        bulkGroups.set(key + "-false", falseOrders);
        bulkGroups.set(key + "-true", trueOrders);
      }

      logger.out.info(
        `scrdpl-s - found ${bulkGroups.size} bulk groups from ${bulkableCount} BM orders(${orderIds.length} total)`,
      );

      // All the requests to send out to the server
      const bulkRequests = [];

      // Map of the new order reference to its original constituent order Refs so we can delete them if successful
      const bulkRequestMap: Map<string, string[]> = new Map();
      // Map of order to delete - at the moment, the server is not returning matching client references
      const toDelete: string[] = [];

      // const toDelete: string[] = [];
      bulkGroups.forEach((orders) => {
        if (orders.length > 1) {
          const orderRefs: string[] = [];
          // We have more than one order with same bulk key so we can bulk them all
          const first = orders[0];
          orderRefs.push(first.request.fixingOrderData.externalClientReference);
          toDelete.push(first.request.fixingOrderData.externalClientReference);
          const primaryOrder = this.lookup.cloneBMOrderFromAllocation(
            first.request,
            first.allocation,
          );
          primaryOrder.fixingOrderData.orderId = null;
          primaryOrder.fixingOrderData.valueDate = null;

          const allocKeys = new Set([first.allocation.allocationReferenceId]);

          const allocations = primaryOrder.fixingOrderData.allocations;
          for (let i = 1; i < orders.length; i++) {
            // Make a copy of the allocation and assign a unique id
            const nextAllocation = Object.assign(
              new CubiqTS.AllocationRequestDataImpl(),
              orders[i].allocation,
            );
            nextAllocation.allocationReferenceId = this.generateAllocationKey(
              nextAllocation.allocationReferenceId,
              allocKeys,
              i,
            );

            allocations.push(nextAllocation);
            orderRefs.push(orders[i].request.fixingOrderData.externalClientReference);
            toDelete.push(orders[i].request.fixingOrderData.externalClientReference);
          }
          bulkRequests.push(primaryOrder);
          bulkRequestMap.set(primaryOrder.fixingOrderData.externalClientReference, orderRefs);
        }
      });

      if (bulkRequests.length > 0) {
        logger.out.info(`scrdpl-s - bulking ${bulkRequests.length} order groups`);

        this.bulkingService.bulkOrders(bulkRequests).then((report) => {
          const bulkedResults = report.results as CubiqTS.FixingOrderRequestImpl[];

          // Array for bulked orders that we've matched with those we are expecting
          const processedResults: CubiqTS.FixingOrderRequestImpl[] = [];
          if (bulkedResults?.length) {
            bulkedResults.forEach((bulkedOrder) => {
              const orderResult = bulkedOrder?.fixingOrderData;
              if (orderResult) {
                if (orderResult.allocations?.length > 0 && !orderResult.accountCode) {
                  // On bulking we might not get an account code so take the first account code available
                  orderResult.accountCode = orderResult.allocations[0].accountCode;
                }

                // We have the bulked order so we can delete the constituent orders
                const memberOrders = bulkRequestMap.get(orderResult.externalClientReference);
                if (memberOrders) {
                  memberOrders.forEach((orderId) => {
                    this.deleteOrderReference(orderId);
                  });
                  processedResults.push(bulkedOrder);
                } else {
                  logger.out.warn(
                    `Cannot find member orders for new order ${orderResult.externalClientReference}. Not processed bulk result`,
                  );
                }
              } else {
                logger.out.warn(
                  `Unsuported value returned in bulk result: bulked order = ${
                    bulkedOrder?.toString() ?? "<NULL>"
                  }`,
                );
              }
            });
            this.addNewBMOrderRequests(processedResults);
          }
          res(processedResults);
        });
      } else {
        res([]);
      }
    });
  }

  /**
   * get an allocation key - prefer to retain existing key but if already exists, create a new key
   * @param currentKey The existing allocation that will be used if unique
   * @param existingKeys Set of currently defined allocation Ids
   * @param index
   * @returns
   */
  private generateAllocationKey(
    currentKey: string,
    existingKeys: Set<string>,
    index: number,
  ): string {
    if (existingKeys.has(currentKey)) {
      // Put a reasonable max length on the ID
      currentKey = currentKey.substring(1, 20);
      currentKey = `${currentKey}_${index}`;
      if (existingKeys.has(currentKey)) {
        // Fine - you get something random, so
        currentKey = this.generateRandomId();
      }
    }
    existingKeys.add(currentKey);
    return currentKey;
  }

  /**
   * Create a key for all bulk-related properties of a ScratchPadOrder enabling similar orders
   * to be grouped
   * @param order
   * @returns
   */
  public getBulkingKey(order: CubiqTS.FixingOrderRequestDataImpl): string {
    const keyObject = {};
    BULK_BENCMARK_KEY_FIELDS.forEach((fieldName) => (keyObject[fieldName] = order[fieldName]));

    // Workaround live events that can have null comments defined by the empty string
    if (keyObject["comment"] == null) {
      keyObject["comment"] = "";
    }
    if (keyObject["comment2"] == null) {
      keyObject["comment2"] = "";
    }

    // Need to encode fixingDate and custom values manually as these may have cubiqspecifc ids
    keyObject["customValues"] = order.customValues.map((value) =>
      this.deleteEncodableProperties(value),
    );
    keyObject["fixingDate"] = this.deleteEncodableProperties(order.fixingDate);
    keyObject["fixingTime"] = this.deleteEncodableProperties(order.fixingTime);

    return JSON.stringify(keyObject);
  }

  /**
   * To remove unique message Ids and lots of useless fields we strip out unwantred properties
   * and return a new object that contains what's left
   *
   * @param value
   * @returns
   */
  private deleteEncodableProperties(value: any): any {
    const {
      majorRevision,
      minorRevision,
      innerEncoding,
      enType,
      encodableType,
      encoder,
      _extendedInfo,
      messageId,
      ...retValue // This will have all the rest of the properties
    } = value;
    return retValue;
  }
}

type BulkableOrder = {
  request: CubiqTS.FixingOrderRequestImpl;
  allocation: CubiqTS.AllocationRequestData;
};
